import * as React from 'react';
import injectSheet from 'react-jss';
import { NAVBAR_HEIGHT, NAVBAR_ZINDEX } from '../../globals';
import { tokens } from 'src/design-system';
import { Button, Icon } from 'antd';
import { connect, ConnectedProps } from 'react-redux';
import { withRouter } from 'react-router';
import { bindActionCreators } from 'redux';
import { toggleSideMenu } from '../../actions/navigationActions';
import Media from 'react-media';
import MenuIcon from '../common/icons/MenuIcon';
import DocumentTitle from 'react-document-title';
import { RouterProps, StylesProps } from 'src/Library/Types';
import NotificationsButton from '../pages/Notifications/NotificationsButton';
import {
  getNotifications,
  getNotificationsPolling,
  markNotifications,
} from 'src/api/shipmentExecution';
import UserAccountButton from './userAccount/UserAccountButton';
// import { getHomeRoute } from 'src/routing/utils';
import CrossIcon from '../common/icons/CrossIcon';
import GetFinancingNowButton from './getFinancingNow/GetFinancingNowButton';
import { mediaDown } from 'src/design-system/mixins/responsive';

// const ShipsySupportUrl = 'https://intercom.help/shipsysupport/en/';
const ShipsySupportUrl = 'https://shipsy-help.freshdesk.com/support/home';

const styles = (theme) => ({
  navbar: {
    height: `${NAVBAR_HEIGHT}px`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0,
    padding: `${theme.spacing.xs}px ${theme.spacing.md}px`,
    border: `1px solid ${theme.colors.border}`,
    color: theme.colors.text,
    backgroundColor: theme.colors.bgWhite,
    zIndex: NAVBAR_ZINDEX,
  },
  hamburger: {
    '&:hover': {
      cursor: 'pointer',
    },
    color: theme.colors.textSecondary,
    fontSize: '24px',
  },
  searchIcon: {
    fontSize: `${theme.fontSize.h1}px`,
  },
  header: {
    // give NAVBAR_HEIGHT to your pageTitle element to vertically align it
    marginLeft: `${theme.spacing.md}px`,
    fontSize: `${theme.fontSize.h1}px`,
    fontWeight: '600',
    height: NAVBAR_HEIGHT - 2,
    width: '100%',
    '& .ant-tabs .ant-tabs-large-bar .ant-tabs-tab': {
      height: NAVBAR_HEIGHT,
    },
  },
  titleString: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  leftFlex: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'flex-start',
    // width: '100%',
  },
  backMenu: {
    position: 'relative',
    zIndex: '501',
  },
  userName: {
    color: theme.colors.bgWhite,
    fontSize: `${theme.fontSize.h3}px`,
    margin: {
      left: '6px',
    },
  },
  ...mediaDown(theme.breakpoint.sm, {
    header: {
      fontSize: `${theme.fontSize.h2}px`,
    },
    navbar: {
      border: 'none',
      padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
      color: theme.colors.bgWhite,
      backgroundColor: theme.colors.primary,
    },
    hamburger: {
      color: theme.colors.bgWhite,
    },
  }),
  // Ant Design tab override for mobile active tab color
  [`@media (max-width: ${theme.breakpoint.sm - 1}px)`]: {
    navbar: {
      '& .ant-tabs-nav .ant-tabs-tab-active': {
        color: theme.colors.bgWhite,
      },
    },
  },
  rightTools: {
    alignItems: 'center',
    display: 'flex',
    position: 'relative',
    zIndex: '501',
  },
  toolContainer: {
    display: 'flex',
    '&:not(:last-child)': {
      marginRight: `${theme.spacing.sm}px`,
    },
  },
  closeIcon: {
    fontSize: 24,
    color: theme.colors.textSecondary,
  },
  icon: {
    fontSize: 24,
    color: theme.colors.textSecondary,
    marginRight: 27,
  },
  extras: {
    display: 'flex',
  },
  cancelButton: {
    marginRight: `${theme.spacing.md}px`,
  },
});

interface NavbarProps extends StylesProps<ReturnType<typeof styles>>, RouterProps, PropsFromRedux {
  pageTitle: string;
  headerTitle: string;
  hideMenuIcon?: boolean;
  handleBack: () => void;
  handleCancelClick: () => void;
  handleSubmitClick: () => void;
  loadingAfterSubmit?: boolean;
  alwaysShowBack: boolean;
  hideRightTools?: boolean;
  shouldDisplayBack: boolean;
  styles: React.CSSProperties;
  components: (string | React.ReactElement)[];
  disableNotification?: boolean;
  helpLink?: string;
  defaultNotificationTab: boolean;
  showProcurement: boolean;
  disableHelp?: boolean;
  disableProfile?: boolean;
  showCancelButton?: boolean;
  showSubmitButton?: boolean;
  // Removed defaultLandingPage from here since it comes from PropsFromRedux
}

const Navbar: React.FC<NavbarProps> = (props) => {
  const {
    open,
    styles,
    classes,
    orgType,
    history,
    pageTitle,
    handleBack,
    headerTitle,
    hideMenuIcon,
    hideRightTools,
    alwaysShowBack,
    toggleSideMenu,
    shouldDisplayBack,
    disableNotification,
    helpLink,
    defaultNotificationTab,
    showProcurement,
    showExecution,
    showRFQ,
    disableHelp,
    disableProfile,
    showGetFinancingButton,
    showCancelButton,
    showSubmitButton,
    handleCancelClick,
    handleSubmitClick,
    loadingAfterSubmit,
  } = props;

  const fallBackHelpLink = ShipsySupportUrl;
  const disableClickStyle: React.CSSProperties = {
    pointerEvents: open ? 'none' : 'auto',
  };
  
  const handleBackClick = () => {
    history.goBack();
  };

  const renderMenuIcon = () => {
    if (hideMenuIcon) {
      return <div style={{ width: 24 }} />;
    }

    if (open) {
      document.body.style.overflow = 'hidden';
      return <CrossIcon className={classes.closeIcon} onClick={toggleSideMenu} />;
    }

    document.body.style.overflow = 'auto';
    return <MenuIcon className={classes.hamburger} onClick={toggleSideMenu} />;
  };

  const BackOrMenu = ({ handleBackClick }) => {
    if (alwaysShowBack) {
      return (
        <Icon
          className={props.classes.hamburger}
          type="arrow-left"
          theme="outlined"
          onClick={handleBackClick}
        />
      );
    }
    if (!shouldDisplayBack) {
      return renderMenuIcon();
    }
    return (
      <Media query={`(max-width: ${tokens.breakpoint.sm}px)`}>
        {(matches) =>
          matches || hideMenuIcon ? (
            <Icon
              className={props.classes.hamburger}
              type="arrow-left"
              theme="outlined"
              onClick={handleBackClick}
            />
          ) : (
            renderMenuIcon()
          )
        }
      </Media>
    );
  };

  const renderNotificationsButton = () => {
    return (
      <NotificationsButton
        styles={{
          marginRight: 27,
        }}
        type={orgType}
        notifApi={getNotifications}
        markApi={markNotifications}
        pollingApi={getNotificationsPolling}
        currentModule={
          defaultNotificationTab ||
          (showProcurement
            ? 'INQUIRY'
            : showExecution
              ? 'SHIPMENT'
              : showRFQ
                ? 'RFQ_PLAN'
                : 'SHIPMENT')
        }
      />
    );
  };

  const renderGetFinancingNowButton = () => {
    return <GetFinancingNowButton/>
  }

  const renderUserAccountButton = () => {
    return <UserAccountButton />;
  };

  const renderHelpButton = () => {
    return (
      <a
        onClick={() => {
          window.open(helpLink || fallBackHelpLink);
        }}
      >
        <Icon className={classes.icon} type="question-circle" theme="filled" />
      </a>
    );
  };

  const renderCancelButton = () => {
    return (
      <Button
      onClick={handleCancelClick}
      className={classes.cancelButton}>
        Cancel
      </Button>
    );
  };

  const renderSubmitButton = () => {
    return (
      <Button
      type='primary'
      onClick={handleSubmitClick}
      loading={loadingAfterSubmit}>
        Submit
      </Button>
    );
  };

  const renderRightTools = () => {
    return (
      <div className={classes.toolContainer}>
        {!hideRightTools && (
          <div className={classes.extras} style={disableClickStyle}>
            {disableNotification ? null : renderNotificationsButton()}
            {/* Commenting help section until. Dev rev help section is ready */}
            {/* {disableHelp ? null : renderHelpButton()} */} 
            {showCancelButton && renderCancelButton()}
            {showSubmitButton && renderSubmitButton()}
          </div>
        )}
        {disableProfile ? null : renderUserAccountButton()}
      </div>
    );
  };

  return (
    <div className={classes.navbar} style={{ ...styles }} id="navbar">
      <DocumentTitle
        title={typeof pageTitle === 'string' ? pageTitle : headerTitle}
      ></DocumentTitle>
      <div className={classes.leftFlex}>
        <span className={classes.backMenu}>
          <BackOrMenu {...props} handleBackClick={handleBack || handleBackClick} />
        </span>
        <span className={classes.header} style={disableClickStyle}>
          {typeof pageTitle === 'string' ? (
            <span className={classes.titleString}>{pageTitle}</span>
          ) : (
            pageTitle
          )}
        </span>
      </div>
      <div className={classes.rightTools}>{renderRightTools()}</div>
    </div>
  );
};

const mapStateToProps = ({ navigation, masterData }) => ({
  open: navigation.sideMenuOpen,
  orgType: masterData.userData.type,
  showProcurement: masterData.modules?.show_inquiry || masterData.modules?.show_inquiry_bidding,
  showExecution:
    masterData.modules?.show_shipment_execution_shipper ||
    masterData.modules?.show_shipment_execution_vendor,
  showAirCargo: masterData.modules?.show_air_tracking,
  showRFQ:
    masterData.modules?.show_rfq ||
    masterData.modules?.show_rfq_rfq_plans ||
    masterData.modules?.show_bidding,
  showGetFinancingButton: masterData.show_get_financing_cta,
  defaultLandingPage: masterData?.modules?.default_route || '',
  userLandingPage: masterData?.userData?.user_metadata?.landing_page || '',
  discoverRatesPrefillData: masterData?.redirectPrefillData
});

const mapDispatchToProps = (dispatch, ownProps) => {
  return bindActionCreators(
    {
      toggleSideMenu,
    },
    dispatch
  );
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const NavbarStyled = injectSheet(styles)(Navbar);
export default withRouter(connector(NavbarStyled));